import SecureLS from 'secure-ls';
import { Device } from "@/utils/Capacitor";
import { Preferences } from '@capacitor/preferences';

const ls = new SecureLS();

const ENCRYPTED_PREFIX = 'enc.v1:'
const KEY_SALT = 'builder.credentials.v1'
const FALLBACK_DEVICE_ID = 'builder.device.fallback'

let credentialsKeyPromise = null

const subtleCrypto = () => (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) || null

const toBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return window.btoa(binary)
}

const fromBase64 = (value) => Uint8Array.from(window.atob(value), char => char.charCodeAt(0))

const credentialsKey = () => {
    const subtle = subtleCrypto()
    if (!subtle) return Promise.resolve(null)
    if (!credentialsKeyPromise) {
        credentialsKeyPromise = (async () => {
            const deviceId = await Device.getId() || FALLBACK_DEVICE_ID
            const encoder = new TextEncoder()
            const material = await subtle.importKey('raw', encoder.encode(deviceId), 'PBKDF2', false, ['deriveKey'])
            return subtle.deriveKey(
                { name: 'PBKDF2', salt: encoder.encode(KEY_SALT), iterations: 100000, hash: 'SHA-256' },
                material,
                { name: 'AES-GCM', length: 256 },
                false,
                ['encrypt', 'decrypt']
            )
        })().catch(() => null)
    }
    return credentialsKeyPromise
}

const encryptCredentials = async (credentials) => {
    const key = await credentialsKey()
    if (!key) return credentials
    const iv = window.crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(JSON.stringify(credentials))
    const cipher = await subtleCrypto().encrypt({ name: 'AES-GCM', iv }, key, encoded)
    return `${ENCRYPTED_PREFIX}${toBase64(iv)}.${toBase64(cipher)}`
}

const decryptCredentials = async (stored) => {
    const key = await credentialsKey()
    if (!key) return null
    const [iv, cipher] = String(stored).slice(ENCRYPTED_PREFIX.length).split('.')
    if (!iv || !cipher) return null
    try {
        const plain = await subtleCrypto().decrypt({ name: 'AES-GCM', iv: fromBase64(iv) }, key, fromBase64(cipher))
        return JSON.parse(new TextDecoder().decode(plain))
    } catch (error) {
        return null
    }
}

export const Storage = {
    getUser: async () => {
        try {
            if (await Device.getPlatform() === 'web') {
                return ls.get(`${process.env.VUE_APP_NAME_ID}_user`);
            } else {
                const { value } = await Preferences.get({ key: `${process.env.VUE_APP_NAME_ID}_user` });
                return value ? JSON.parse(value) : null;
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            return null;
        }
    },
    setUser: async (user = null) => {
        try {
            if (await Device.getPlatform() === 'web') {
                ls.set(`${process.env.VUE_APP_NAME_ID}_user`, user);
            } else {
                await Preferences.set({
                    key: `${process.env.VUE_APP_NAME_ID}_user`,
                    value: JSON.stringify(user)
                });
            }
        } catch (error) {
            console.error('Error saving user:', error);
        }
    },
    getAccount: async () => {
        try {
            if (await Device.getPlatform() === 'web') {
                return ls.get(`${process.env.VUE_APP_NAME_ID}_account`);
            } else {
                const { value } = await Preferences.get({ key: `${process.env.VUE_APP_NAME_ID}_account` });
                return value ? JSON.parse(value) : null;
            }
        } catch (error) {
            console.error('Error fetching account:', error);
            return null;
        }
    },
    setAccount: async (account = null) => {
        try {
            if (await Device.getPlatform() === 'web') {
                ls.set(`${process.env.VUE_APP_NAME_ID}_account`, account);
            } else {
                await Preferences.set({
                    key: `${process.env.VUE_APP_NAME_ID}_account`,
                    value: JSON.stringify(account)
                });
            }
        } catch (error) {
            console.error('Error saving account:', error);
        }
    },
    getBrandLogo: async () => {
        try {
            if (await Device.getPlatform() === 'web') {
                return ls.get(`${process.env.VUE_APP_NAME_ID}_brandLogo`);
            } else {
                const { value } = await Preferences.get({ key: `${process.env.VUE_APP_NAME_ID}_brandLogo` });
                return value ? JSON.parse(value) : null;
            }
        } catch (error) {
            console.error('Error fetching brandLogo:', error);
            return null;
        }
    },
    setBrandLogo: async (brandLogo = null) => {
        try {
            if (await Device.getPlatform() === 'web') {
                ls.set(`${process.env.VUE_APP_NAME_ID}_brandLogo`, brandLogo);
            } else {
                await Preferences.set({
                    key: `${process.env.VUE_APP_NAME_ID}_brandLogo`,
                    value: JSON.stringify(brandLogo)
                });
            }
        } catch (error) {
            console.error('Error saving brandLogo:', error);
        }
    },
    clear: async () => {
        try {
            await Storage.setUser(null);
            await Storage.setAccount(null);
            await Storage.setBrandLogo(null);
        } catch (error) {
            console.error('Error clearing data:', error);
        }
    },
    getCredentials: async () => {
        try {
            let stored = null
            if (await Device.getPlatform() === 'web') {
                stored = ls.get(`${process.env.VUE_APP_NAME_ID}_credentials`);
            } else {
                const { value } = await Preferences.get({ key: `${process.env.VUE_APP_NAME_ID}_credentials` });
                stored = value ? JSON.parse(value) : null;
            }
            if (!stored) return null;
            if (typeof stored === 'string' && stored.startsWith(ENCRYPTED_PREFIX)) {
                const credentials = await decryptCredentials(stored);
                if (!credentials) await Storage.setCredentials(null);
                return credentials;
            }
            if (typeof stored === 'object' && stored.email && stored.password) {
                if (await credentialsKey()) await Storage.setCredentials(stored);
                return stored;
            }
            return null;
        } catch (error) {
            console.error('Error fetching credentials:', error);
            return null;
        }
    },
    setCredentials: async (credentials = null) => {
        try {
            const payload = credentials ? await encryptCredentials(credentials) : null;
            if (await Device.getPlatform() === 'web') {
                ls.set(`${process.env.VUE_APP_NAME_ID}_credentials`, payload);
            } else {
                await Preferences.set({
                    key: `${process.env.VUE_APP_NAME_ID}_credentials`,
                    value: JSON.stringify(payload)
                });
            }
        } catch (error) {
            console.error('Error saving credentials:', error);
        }
    },
    getRoutineRemindersSync: async () => {
        try {
            if (await Device.getPlatform() === 'web') {
                return ls.get(`${process.env.VUE_APP_NAME_ID}_routineRemindersSync`);
            } else {
                const { value } = await Preferences.get({ key: `${process.env.VUE_APP_NAME_ID}_routineRemindersSync` });
                return value ? JSON.parse(value) : null;
            }
        } catch (error) {
            console.error('Error fetching routineRemindersSync:', error);
            return null;
        }
    },
    setRoutineRemindersSync: async (routineRemindersSync = null) => {
        try {
            if (await Device.getPlatform() === 'web') {
                ls.set(`${process.env.VUE_APP_NAME_ID}_routineRemindersSync`, routineRemindersSync);
            } else {
                await Preferences.set({
                    key: `${process.env.VUE_APP_NAME_ID}_routineRemindersSync`,
                    value: JSON.stringify(routineRemindersSync)
                });
            }
        } catch (error) {
            console.error('Error saving routineRemindersSync:', error);
        }
    },
};
